import React from "react";

import { assertNever } from "@impero-com/common/utils/obj";
import { Typography } from "antd";
import { isEqual } from "lodash-es";

import { MultiInputHelper } from "utils/multiInputHelper";

import { BaseFormDisplay } from "./formDisplay";
import { SelectInputOption, RawSelectInput } from "./selectInput";

const helper: MultiInputHelper<string, string> = new MultiInputHelper({
    isEqual: (a: string, b: string) => a === b,
    isBlank: (val: string) => /^\s*$/.test(val),
    display: (value: string) => value,
});

type MultiSelectInputProps =
    | ({ type: "EDIT" } & WrappedWriteProps)
    | ({ type: "READ_ONLY" } & WrappedReadProps);

type Wrapped<T> = T & { label: string; helpText?: string };

interface CommonProps {
    name: string;
    values?: string[];
    options: SelectInputOption[];
    className?: string;
}

interface ReadProps extends CommonProps {
    display: "Text" | "Input";
}

type WrappedReadProps = Wrapped<ReadProps>;

interface WriteProps extends CommonProps {
    onChange: (v: string | undefined) => void;
    dropdownMatchSelectWidth?: boolean;
    showSearch?: boolean;
}

type WrappedWriteProps = Wrapped<WriteProps>;

class MultiSelectInput extends React.PureComponent<MultiSelectInputProps> {
    render() {
        const { props } = this;
        switch (props.type) {
            case "READ_ONLY": {
                const {
                    label,
                    helpText,
                    name,
                    values,
                    options,
                    display,
                    className,
                } = props;
                return (
                    <WrappedReadOnlySelectInput
                        display={display}
                        label={label}
                        helpText={helpText}
                        name={name}
                        values={values}
                        options={options}
                        className={className}
                    />
                );
            }
            case "EDIT": {
                const {
                    label,
                    helpText,
                    name,
                    values,
                    onChange,
                    options,
                    className,
                    dropdownMatchSelectWidth,
                    showSearch,
                } = props;
                return (
                    <WrappedEditSelectInput
                        label={label}
                        helpText={helpText}
                        name={name}
                        values={values}
                        onChange={onChange}
                        options={options}
                        className={className}
                        dropdownMatchSelectWidth={dropdownMatchSelectWidth}
                        showSearch={showSearch}
                    />
                );
            }
            default:
                return assertNever(props);
        }
    }
}

type RawReadOnlySelectInputProps = ReadProps;

class RawReadOnlySelectInput extends React.PureComponent<RawReadOnlySelectInputProps> {
    render() {
        const { display, values, options, className } = this.props;
        switch (display) {
            case "Text": {
                const { value, type } =
                    helper.deriveReadOnlyValueAndType(values);
                return <Typography.Text type={type}>{value}</Typography.Text>;
            }
            case "Input": {
                const { value, placeholder } =
                    helper.deriveValueAndPlaceholder(values);
                return (
                    <RawSelectInput
                        value={value}
                        options={options}
                        placeholder={placeholder}
                        className={className}
                        type={"READ_ONLY"}
                    />
                );
            }
            default: {
                return assertNever(display);
            }
        }
    }
}

interface RawEditableSelectInputState {
    derivedValue: string | undefined;
    trackedValue: string | undefined;
    placeholder: string;
}

class RawEditableSelectInput extends React.Component<
    WriteProps,
    RawEditableSelectInputState
> {
    state: RawEditableSelectInputState = {
        derivedValue: undefined,
        trackedValue: undefined,
        placeholder: "",
    };

    static getDerivedStateFromProps(
        props: WriteProps,
        state: RawEditableSelectInputState
    ): RawEditableSelectInputState | null {
        const { value, placeholder } = helper.deriveValueAndPlaceholder(
            props.values
        );
        if (
            !isEqual(value, state.derivedValue) ||
            placeholder !== state.placeholder
        ) {
            return {
                derivedValue: value,
                trackedValue: value,
                placeholder,
            };
        }
        return null;
    }

    render() {
        const { state, props } = this;
        const { trackedValue, placeholder } = state;
        const {
            options,
            name,
            className,
            dropdownMatchSelectWidth,
            showSearch,
        } = props;
        return (
            <RawSelectInput
                id={name}
                className={className}
                value={trackedValue}
                onChange={this.onChange}
                options={options}
                placeholder={placeholder}
                type={"EDIT"}
                dropdownMatchSelectWidth={dropdownMatchSelectWidth}
                showSearch={showSearch}
            />
        );
    }
    onChange = (value: string | undefined): void | Promise<unknown> => {
        this.setState({
            trackedValue: value,
        });
        this.props.onChange(value);
    };
}

class WrappedReadOnlySelectInput extends React.PureComponent<WrappedReadProps> {
    render() {
        const { label, helpText, name, values, options, display, className } =
            this.props;
        return (
            <BaseFormDisplay label={label} helpText={helpText}>
                <RawReadOnlySelectInput
                    display={display}
                    name={name}
                    values={values}
                    options={options}
                    className={className}
                />
            </BaseFormDisplay>
        );
    }
}

class WrappedEditSelectInput extends React.PureComponent<WrappedWriteProps> {
    render() {
        const { label, helpText } = this.props;
        return (
            <BaseFormDisplay label={label} helpText={helpText}>
                {this.getComponent()}
            </BaseFormDisplay>
        );
    }

    getComponent = (): React.ReactNode => {
        const {
            name,
            values,
            onChange,
            options,
            className,
            dropdownMatchSelectWidth,
            showSearch,
        } = this.props;
        return (
            <RawEditableSelectInput
                name={name}
                values={values}
                onChange={onChange}
                options={options}
                className={className}
                dropdownMatchSelectWidth={dropdownMatchSelectWidth}
                showSearch={showSearch}
            />
        );
    };
}

type RawMultiSelectInputProps =
    | ({ type: "EDIT" } & WriteProps)
    | ({ type: "READ_ONLY" } & ReadProps);

class RawMultiSelectInput extends React.PureComponent<RawMultiSelectInputProps> {
    render() {
        const props = this.props;
        switch (props.type) {
            case "EDIT": {
                const {
                    name,
                    values,
                    options,
                    onChange,
                    className,
                    dropdownMatchSelectWidth,
                    showSearch,
                } = props;
                return (
                    <RawEditableSelectInput
                        name={name}
                        values={values}
                        options={options}
                        onChange={onChange}
                        className={className}
                        dropdownMatchSelectWidth={dropdownMatchSelectWidth}
                        showSearch={showSearch}
                    />
                );
            }
            case "READ_ONLY": {
                const { name, values, options, display, className } = props;
                return (
                    <RawReadOnlySelectInput
                        display={display}
                        name={name}
                        values={values}
                        options={options}
                        className={className}
                    />
                );
            }
            default: {
                return assertNever(props);
            }
        }
    }
}

export {
    MultiSelectInput,
    type MultiSelectInputProps,
    RawMultiSelectInput,
    type RawMultiSelectInputProps,
};
